import * as React from 'react'
import { validateCommunityAttachmentFile } from '../utils/communityAttachments'

export type CommunityDraftAttachment = {
  id: string
  file: File
  kind: 'image' | 'file'
  previewUrl?: string
}

function buildAttachmentId(file: File): string {
  const name = String(file?.name || '').trim()
  const size = typeof file?.size === 'number' ? file.size : 0
  const lastModified = typeof file?.lastModified === 'number' ? file.lastModified : 0
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${name}-${size}-${lastModified}`
}

function revokePreviewUrl(url?: string) {
  if (!url) return
  if (!url.startsWith('blob:')) return
  try {
    URL.revokeObjectURL(url)
  } catch {
    // ignore
  }
}

export function useCommunityDraftAttachments() {
  const [attachments, setAttachments] = React.useState<CommunityDraftAttachment[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const attachmentsRef = React.useRef<CommunityDraftAttachment[]>([])

  React.useEffect(() => {
    attachmentsRef.current = attachments
  }, [attachments])

  const addFiles = React.useCallback((files: File[]) => {
    const list = Array.isArray(files) ? files.filter(Boolean) : []
    if (!list.length) return

    const next: CommunityDraftAttachment[] = []
    const rejected: { name: string; reason: string }[] = []

    list.forEach((file) => {
      const res = validateCommunityAttachmentFile(file)
      if (!res.ok) {
        rejected.push({ name: String(file?.name || 'arquivo'), reason: res.reason })
        return
      }

      const id = buildAttachmentId(file)
      const previewUrl = res.kind === 'image' ? URL.createObjectURL(file) : undefined
      next.push({ id, file, kind: res.kind, previewUrl })
    })

    if (rejected.length) {
      const first = rejected[0]
      const suffix = rejected.length > 1 ? ` (+${rejected.length - 1})` : ''
      setError(`${first.name}: ${first.reason}${suffix}`)
    } else if (error) {
      setError(null)
    }

    if (next.length) {
      setAttachments((prev) => [...prev, ...next])
    }
  }, [error])

  const removeAttachment = React.useCallback((id: string) => {
    setAttachments((prev) => {
      const current = Array.isArray(prev) ? prev : []
      const toRemove = current.find((att) => att.id === id)
      if (toRemove?.previewUrl) revokePreviewUrl(toRemove.previewUrl)
      return current.filter((att) => att.id !== id)
    })
  }, [])

  const clear = React.useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((att) => revokePreviewUrl(att.previewUrl))
      return []
    })
    setError(null)
  }, [])

  React.useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((att) => revokePreviewUrl(att.previewUrl))
    }
  }, [])

  return {
    attachments,
    addFiles,
    removeAttachment,
    clear,
    error,
  }
}
